import MD5 from 'crypto-js/md5'

const fragmentsLimit = 1000

/**
 * apply fragments to query
 * @param {Object} request this is the query requested.
 * @param {Object} fragments this is the fragments related to query.
 * @return {Object} the requested modified with fragments applyed
 */

function applyFragments(request, fragments) {
  var sortedFragments = Object.keys(fragments)
  sortedFragments.sort(function (a, b) {
    return a.length < b.length
  })
  var continueApplying = true
  var replacedFragments = 0

  while (continueApplying) {
    if (request.match(/\.\.\./)) {
      sortedFragments.forEach(function (name) {
        var escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        request = request.replace(new RegExp(escapedName, 'g'), extractFragmentBody(fragments[name]))
      })
      replacedFragments++

      if (replacedFragments > fragmentsLimit) {
        continueApplying = false
      }
    } else {
      continueApplying = false
    }
  }

  return request
}

/**
 * Extracts fragment body.
 * @param {Object} fragment this is the complete fragment.
 * @return {Object} returns only the fragment body
 */

function extractFragmentBody(fragment) {
  var fragmentBody = fragment.replace(/fragment(.|\n)*?{/gm, '')
  return fragmentBody.replace(/}(\s|\n)*$/gm, '')
}

/**
 * Extract and group fragment in a hash.
 * @param {Object} request this is the query requested.
 * @return {Object} returns a hash with fragments
 */

 function extractAndGroupFragments(request) {
  var fragments = {}
  var fragmentsStartRegex = /fragment(.|\n)*?{/gm
  var fragmentResult

  while (fragmentResult = fragmentsStartRegex.exec(request)) {
    var openedLevels = 1
    var closedLevels = 0
    var currentIndex = fragmentResult.index + fragmentResult[0].length

    while (openedLevels > closedLevels) {
      if (request[currentIndex] === undefined) {
        break
      }

      currentIndex++

      if (request[currentIndex] === '}') {
        closedLevels++
      } else if (request[currentIndex] === '{') {
        openedLevels++
      }
    }

    var fragmentText = request.substring(fragmentResult.index, currentIndex + 1)
    var fragmentName = extractFragmentName(fragmentText)
    fragments[fragmentName] = fragmentText
  }

  return fragments
}

/**
 * Extract the name of fragment
 * @param {Object} fragment this is the query requested.
 * @return {Object} Returns the fragment name with ... prefix
 */

function extractFragmentName(fragment) {
  var fragmentName = fragment.replace(/fragment(\s|\n){1,}/gm, '')
  return '...' + fragmentName.replace(/\s{1,}on(.|\n)*/gm, '')
}

/**
 * Remove all fragments that already replaced on the current query.
 * @param {Object} request this is the query requested.
 * @param {Object} fragments this is the fragments related to query.
 * @return {Object} returns the query without the fragments.
 */

function removeFragments(request, fragments) {
  Object.keys(fragments).forEach(function (name) {
    request = request.replace(fragments[name], '')
  })
  return request
}

/**
 * Remove commas from query.
 * @param {Object} request this is the query requested.
 * @return {Object} the query without commas
 */

function removeCommas(request) {
  return request.replace(/,/g, '\n')
}

/**
 * Normalize spaces removing all of them.
 * @param {Object} request this is the query requested.
 * @return {Object} The sum of the two numbers.
 */

function normalizeSpaces(request) {
  return request.replace(/\s{1,}/g, '\n')
}

/**
 * Sanitize query and remove lines from query.
 * @param {Object} request this is the query requested.
 * @return {Object} the query without \n.
 */

function removeLines(request) {
  return request.replace(/\n{1,}/g, ' ')
}

/**
 * Remove query data.
 * @param {Object} request this is the query requested.
 * @return {Object} removed query name.
 */

function removeQuery(request) {
  request = request.replace(/^(.|\n)*?{/m, '')
  return request.replace(/}(\s|\n)*$/m, '')
}

/**
 * Normalize variable replacing all variables with letter map
 * so, this transforms the variable $variable to $a and will be used
 * to generate the same hash even the variable's name was changed
 * @param {Object} query this is the query requested.
 * @return {Object} the query with variable replaced
 */

function normalizeVariables(query) {
  var variablesMap = {}
  var variablesLetters = { 0: '$a', 1: '$b', 2: '$c', 3: '$d', 4: '$e', 5: '$f', 6: '$g', 7: '$g', 8: '$g', 9: '$g' }
  var index = 0
  var variable
  var variableQueryRegex = /\$.+?\b/gm

  while (variable = variableQueryRegex.exec(query)) {
    if (!variablesMap[variable]) {
      variablesMap[variable] = variablesLetters[index]
      index++
    }
  }

  Object.keys(variablesMap).forEach(function (originalVariable) {
    var newVariable = variablesMap[originalVariable]
    query = query.replace(originalVariable + ')', newVariable + ')')
    query = query.replace(originalVariable + ':', newVariable + ':')
    query = query.replace(originalVariable + ' ', newVariable + ' ')
  })
  return query
}

/**
 * This parses the query and generate the hash.
 * @param {Object} query this is the query requested.
 * @param {Object} variables this is the variables passed in request data.
 * @return {Object} the md5 hash of graphql query.
 */

export default function generateRequestHash(query) {
  var anotherQuery = applyFragments(query, extractAndGroupFragments(query))
  anotherQuery = removeFragments(anotherQuery, extractAndGroupFragments(anotherQuery))
  anotherQuery = removeCommas(anotherQuery)
  anotherQuery = normalizeSpaces(anotherQuery)
  anotherQuery = removeQuery(anotherQuery)
  anotherQuery = removeLines(anotherQuery)

  //one line query
  anotherQuery = anotherQuery.replace(/\s{1,}/gm, '')

  var letters = anotherQuery.split('')
  letters.sort()

  return MD5(letters.join('')).toString()
}
